import { Component, Inject, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../../shared/services/userData.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { CustomEopService } from '../../../shared/services/customEop.service';
import { ICustomEopSection } from '../../../shared/models/eopSection';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { FormsModule } from '@angular/forms';
import { CustomFileUploadComponent } from "./custom-file-upload/custom-file-upload.component";
import { EditCustomPageComponent } from './edit-custom-page/edit-custom-page.component';
import { EopNavigationService } from '../../../shared/services/eopNavigation.service';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { FilesService } from '../../../shared/services/files.service';
import { File } from '../../../shared/models/schedule';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { IUserInfo } from '../../../shared/models/contact';
import { NoEmojiDirective } from '../../../shared/helpers/noEmoji.directive';

@Component({
  selector: 'custom-page',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    PopupMessageComponent,
    PrvNextButtonsComponent,
    LucideAngularModule,
    FormsModule,
    CustomFileUploadComponent,
    EditCustomPageComponent,
    IntroductionComponent,
    DeleteConfirmationComponent,
    NoEmojiDirective
],
  templateUrl: './custom-page.component.html',
  styleUrl: './custom-page.component.css'
})
export class CustomPageComponent implements OnInit {
  private readonly activatedRoute = inject(ActivatedRoute);

  icons = Icons;

  content: string = '';
  currentCustomPageId: string | null = null;
  currentCustomPage: ICustomEopSection | null = null;
  currentUser: IUserInfo | null = null;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  selectedFile: File | null = null;

  loading: boolean = false;
  showEditCustomPage: boolean = false;
  showCustomFileUpload: boolean = false;
  showDeleteConfirmation: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private filesService: FilesService,
    private customEopService: CustomEopService,
    public eopNavigationService: EopNavigationService,
    private userDataService: UserDataService
  ) {}

  ngOnInit(): void {
    this.currentUser = this.userDataService.getUserData();
    this.activatedRoute.paramMap.subscribe((params) => {
      const newId = params.get('customPageId');
      if (newId && newId !== this.currentCustomPageId) {
        this.currentCustomPageId = newId;
        this.loadCustomPage();
      }
    });
  }

  loadCustomPage(): void {
    if (this.currentCustomPageId) { 
      this.loading = true;
      this.customEopService.getCustomPageByPageId(this.currentCustomPageId).subscribe({
        next: (response) => {
          this.currentCustomPage = {
          ...response.data,
          files: response.data.files ?? []
        };
          this.content = response.data?.content || '';
          console.table([this.currentCustomPage]);
          console.log('Custom page data:', response.data);
          this.loading = false;
        },
        error: (error) => {
          this.router.navigate(['/not-found']);
          console.error('Error loading custom page:', error.message);
        }
      })
    }
  }

  handleOpenModal(modalName: string, item?: any): void {
    if (modalName === 'custom-file-upload') {
      this.showCustomFileUpload = true;
    } else if (modalName === 'edit-custom-page') {
      this.showEditCustomPage = true;
    } else if (modalName === 'delete-confirmation') {
      this.showDeleteConfirmation = true;
      this.selectedFile = item;
    }
  }

  handleCloseModal(modalName: string): void {
    if (modalName === 'custom-file-upload') {
      this.showCustomFileUpload = false;
    } else if (modalName === 'edit-custom-page') {
      this.showEditCustomPage = false;
    } else if (modalName === 'delete-confirmation') {
      this.showDeleteConfirmation = false;
      this.selectedFile = null;
    }
  }
    
  downloadFile(file: File): void {
    this.loading = true;
    this.filesService.downloadFile(String(file.building_id), String(file.section_id), file.id).subscribe({
      next: (res) => {
        this.loading = false;
        if (!res.has_error && res.data?.signed_url) {
          window.open(res.data.signed_url, '_blank');
        } else {
          console.error('Error downloading file:', res.message);
        }
      },
      error: (err) => {
        this.loading = false;
        console.error('Download failed:', err);
      }
    });
  }

  deleteFile(file: File): void {
    if (file) {
      this.filesService.deleteFile(String(file.building_id), String(file.section_id), file.id).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.messageModal = "success";
            this.popupMessage = 'This occurred after deleting a file.';
            this.loadCustomPage();
            this.handleCloseModal('delete-confirmation');
          }
        },
        error: (error) => {
          this.loading = false;
          this.messageModal = "unsuccess";
          this.popupMessage = error.message;
          console.error('Error deleting the file:', error.message);
        } 
      })
    }
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    this.handleCloseModal(actionType);
    this.loadCustomPage();

    if (message.type === 'success') {
      this.loadCustomPage();
      this.messageModal = 'success';
      this.popupMessage = message.message;
      
    } else if (message.type === 'unsuccess') {
      this.messageModal = 'unsuccess';
      this.popupMessage = message.message;
    } 
  }

  onSubmit(): void {
    if (!this.currentCustomPageId || !this.content.trim()) {
      return;
    }
  
    const updateData = { content: this.content };
  
    this.loading = true;
    this.customEopService.updateCustomPage(this.currentCustomPageId, updateData).subscribe({
      next: (response) => {
        this.loading = false;
        this.messageModal = 'success';
        this.popupMessage = 'This occurred after updating a custom document.';
        this.loadCustomPage();
      },
      error: (error) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }
  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

  get isContentUnchanged(): boolean {
    return this.content.trim() === this.currentCustomPage?.content?.trim();
  }

  formatFileSize(size: number, decimals: number = 2): string {
    if (!size) return '0 B';
  
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
  
}
